import type { ColumnDefinition, EntityFilterConfig } from "../../../ui/components/Table/types";
import type { AnalyticsSession, AnalyticsErrorEvent } from "../../lib/api/product-analytics";
import type { TranslateValues } from "../../lib/i18n";
import { UserCell, OrganizationCell } from "./productAnalyticsEntityCells";

interface ProductAnalyticsEntityFilters {
  user?: EntityFilterConfig;
  organization?: EntityFilterConfig;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function formatDuration(ms: number | null): string {
  if (ms == null) return "—";
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rem = seconds % 60;
  return `${minutes}m ${rem}s`;
}

/**
 * appSource is deliberately an open string on the backend (not a fixed enum)
 * so a future `ios`/`android` value slots in here as one more filter option
 * with zero schema/API rework — see analytics-service's schema.ts comment.
 */
const APP_SOURCE_OPTIONS = [
  { value: "pro-web", label: "Lion Pro" },
  { value: "smart-web", label: "Lion Smart" },
];

function appSourceLabel(value: string): string {
  return APP_SOURCE_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

export function createSessionColumns(
  t: (key: string, values?: TranslateValues) => string,
  accessToken: string,
  entityFilters?: ProductAnalyticsEntityFilters,
): ColumnDefinition<AnalyticsSession>[] {
  return [
    {
      id: "user",
      label: t("pro.system.productAnalytics.col.user"),
      pinnedLeft: true,
      filterType: entityFilters?.user ? "entity" : undefined,
      filterKey: "userIds",
      entityFilter: entityFilters?.user,
      render: (row) => <UserCell userId={row.userId} accessToken={accessToken} />,
    },
    {
      id: "startedAt",
      label: t("pro.system.productAnalytics.col.started"),
      sortable: true,
      sortKey: "startedAt",
      render: (row) => (
        <span style={{ fontFamily: "monospace", fontSize: 12 }}>
          {formatTime(row.startedAt)}
        </span>
      ),
    },
    {
      id: "durationMs",
      label: t("pro.system.productAnalytics.col.duration"),
      render: (row) => formatDuration(row.durationMs),
    },
    {
      id: "appSource",
      label: t("pro.system.productAnalytics.col.appSource"),
      filterType: "checkbox",
      filterOptions: APP_SOURCE_OPTIONS,
      render: (row) => appSourceLabel(row.appSource),
    },
    {
      id: "organization",
      label: t("pro.system.productAnalytics.col.organization"),
      filterType: entityFilters?.organization ? "entity" : undefined,
      filterKey: "organizationIds",
      entityFilter: entityFilters?.organization,
      render: (row) => <OrganizationCell organizationId={row.organizationId} accessToken={accessToken} />,
    },
    {
      id: "urlPath",
      label: t("pro.system.productAnalytics.col.page"),
      render: (row) => (
        <span style={{ fontFamily: "monospace", fontSize: 12 }}>{row.urlPath ?? "—"}</span>
      ),
    },
    {
      id: "hasErrors",
      label: t("pro.system.productAnalytics.col.errors"),
      filterType: "checkbox",
      filterOptions: [
        { value: "true", label: t("pro.system.productAnalytics.hasErrors.yes") },
        { value: "false", label: t("pro.system.productAnalytics.hasErrors.no") },
      ],
      render: (row) =>
        row.hasErrors ? (
          <span style={{ color: "var(--color-danger, #DC2626)", fontWeight: 600 }}>
            {t("pro.system.productAnalytics.hasErrors.yes")}
          </span>
        ) : (
          <span style={{ color: "var(--color-fgMuted)" }}>—</span>
        ),
    },
    {
      id: "status",
      label: t("pro.system.productAnalytics.col.status"),
      render: (row) => row.status,
    },
    {
      id: "totalBytes",
      label: t("pro.system.productAnalytics.col.size"),
      defaultHidden: true,
      render: (row) => `${(row.totalBytes / 1024).toFixed(0)} KB`,
    },
  ];
}

const KIND_OPTIONS = [
  { value: "js_error", label: "JS error" },
  { value: "unhandled_rejection", label: "Unhandled rejection" },
  { value: "network_error", label: "Network error" },
];

const SEVERITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

export function createErrorColumns(
  t: (key: string, values?: TranslateValues) => string,
  accessToken: string,
  entityFilters?: ProductAnalyticsEntityFilters,
): ColumnDefinition<AnalyticsErrorEvent>[] {
  return [
    {
      id: "user",
      label: t("pro.system.productAnalytics.col.user"),
      pinnedLeft: true,
      filterType: entityFilters?.user ? "entity" : undefined,
      filterKey: "userIds",
      entityFilter: entityFilters?.user,
      render: (row) => <UserCell userId={row.userId} accessToken={accessToken} />,
    },
    {
      id: "occurredAt",
      label: t("pro.system.productAnalytics.col.occurred"),
      sortable: true,
      sortKey: "occurredAt",
      render: (row) => (
        <span style={{ fontFamily: "monospace", fontSize: 12 }}>
          {formatTime(row.occurredAt)}
        </span>
      ),
    },
    {
      id: "kind",
      label: t("pro.system.productAnalytics.col.kind"),
      filterType: "checkbox",
      filterOptions: KIND_OPTIONS,
      render: (row) => KIND_OPTIONS.find((o) => o.value === row.kind)?.label ?? row.kind,
    },
    {
      id: "message",
      label: t("pro.system.productAnalytics.col.message"),
      render: (row) => (
        <span style={{ fontFamily: "monospace", fontSize: 12 }}>{row.message}</span>
      ),
    },
    {
      id: "severity",
      label: t("pro.system.productAnalytics.col.severity"),
      filterType: "checkbox",
      filterOptions: SEVERITY_OPTIONS,
      render: (row) => {
        const color =
          row.severity === "critical" || row.severity === "high"
            ? "var(--color-danger, #DC2626)"
            : row.severity === "medium"
              ? "var(--color-warning, #D97706)"
              : "var(--color-fgMuted)";
        return <span style={{ color, fontWeight: 600 }}>{row.severity}</span>;
      },
    },
    {
      id: "appSource",
      label: t("pro.system.productAnalytics.col.appSource"),
      filterType: "checkbox",
      filterOptions: APP_SOURCE_OPTIONS,
      render: (row) => appSourceLabel(row.appSource),
    },
    {
      id: "organization",
      label: t("pro.system.productAnalytics.col.organization"),
      filterType: entityFilters?.organization ? "entity" : undefined,
      filterKey: "organizationIds",
      entityFilter: entityFilters?.organization,
      render: (row) => <OrganizationCell organizationId={row.organizationId} accessToken={accessToken} />,
    },
    {
      id: "httpStatus",
      label: t("pro.system.productAnalytics.col.httpStatus"),
      defaultHidden: true,
      render: (row) => row.httpStatus ?? "—",
    },
    {
      id: "urlPath",
      label: t("pro.system.productAnalytics.col.page"),
      defaultHidden: true,
      render: (row) => row.urlPath ?? "—",
    },
  ];
}
